import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { fetchEntityTabs, updateEntityForm, userSelectedEntityList } from "../services/api";
import { useParams, useBlocker } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Select from "react-select";


const EntityForm = () => {
  const { id } = useParams();

  const [tabs, setTabs] = useState({});
  const [activeTab, setActiveTab] = useState("");
  const [activeTabSheet, setActiveTabSheet] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isUpdatingField, setIsUpdatingField] = useState(false);
  const scrollContainerRef = useRef(null);
  const formScrollRef = useRef(null);
  const [entityId] = useState(id);
  const [showNewFieldWarning, setShowNewFieldWarning] = useState(false);
  const pendingTabChangeRef = useRef(null);
  const [ignoreNewFieldWarningOnce, setIgnoreNewFieldWarningOnce] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState(null);
  const [entityInfo, setEntityInfo] = useState({});

  // Sticky scrollbar state
  const [stickyScrollbars, setStickyScrollbars] = useState({});
  const horizontalScrollRefs = useRef({});
  const [isDragging, setIsDragging] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);

  // Add state for the current visible section
  const [currentVisibleSection, setCurrentVisibleSection] = useState(null);

  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState({});


  // Highlight new fields
  const prevFieldNamesRef = useRef({});
  const [newFieldNames, setNewFieldNames] = useState(new Set());
  const justUpdatedRef = useRef(false);

  // Modal logic for navigation block
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingTxRef = useRef(null);
  const [checkedNewFields, setCheckedNewFields] = useState({});
  const [seenNewFields, setSeenNewFields] = useState({});
  const isEmptyValue = (v) => v === undefined || v === null || v === "";

  const hasChanges = () =>
    Object.values(changedFields).some(
      (sheet) => sheet && Object.keys(sheet).length > 0
    );

  const blocker = useBlocker(hasChanges());

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowUnsavedModal(true);
      pendingTxRef.current = blocker;
    }
  }, [blocker.state]);


  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [changedFields]);

  useEffect(() => {
    setCheckedNewFields((prev) => {
      const newObj = { ...prev };
      const allowedFields = new Set(formFields.map(f => f.name));
      Object.keys(newObj).forEach(sheet => {
        Object.keys(newObj[sheet] || {}).forEach(fname => {
          if (!allowedFields.has(fname)) delete newObj[sheet][fname];
        });
      });
      return newObj;
    });
    // No need to clear seenNewFields; you want to keep "ever seen"!
  }, [activeTabSheet, formFields]);

  useEffect(() => {
    if (ignoreNewFieldWarningOnce && pendingTabChange) {
      handleTabClick(pendingTabChange);
      setPendingTabChange(null);
    }
  }, [ignoreNewFieldWarningOnce, pendingTabChange]);

  const handleModalDiscard = () => {
    setChangedFields({});
    setShowUnsavedModal(false);
    if (pendingTxRef.current) {
      pendingTxRef.current.proceed();
      pendingTxRef.current = null;
    }
  };

  const handleModalUpdate = async () => {
    await handleUpdate();
    setShowUnsavedModal(false);
    if (pendingTxRef.current) {
      pendingTxRef.current.proceed();
      pendingTxRef.current = null;
    }
  };

  // --- Fetch and set up tabs/fields ---
  const loadTabs = async (changeActiveTab = true) => {
    try {
      setIsLoadingForm(true);

      const res = await fetchEntityTabs(entityId);
      const tabDataArray = res?.data || [];

      const tabData = {};
      tabDataArray.forEach((tab) => {
        const tabName = tab.tab_name;
        const rawFields = tab.form_data;
        tabData[tabName] = {
          sheet_name: tab.sheet_name,
          fields: rawFields,
        };
      });

      setTabs(tabData);

      // Fetch entity info
      const entityRes = await userSelectedEntityList();
      const entities = entityRes?.data || [];
      const entity = entities.find(e => e.id == entityId);
      if (entity) {
        setEntityInfo(entity);
      }

      const firstKey = Object.keys(tabData)[0];
      if (firstKey) {
        // --- keep same tab active if possible ---
        let tabToShow = firstKey;
        let tabSheetToShow = tabData[firstKey].sheet_name;
        let fieldsToShow = tabData[firstKey].fields;

        if (!changeActiveTab && activeTab && tabData[activeTab]) {
          tabToShow = activeTab;
          tabSheetToShow = tabData[activeTab].sheet_name;
          fieldsToShow = tabData[activeTab].fields;
        }

        setActiveTab(tabToShow);
        setActiveTabSheet(tabSheetToShow);
        setFormFields(fieldsToShow);

        // -- refresh all formData from API on first load or after update --
        const allNewFormData = {};
        Object.values(tabData).forEach(tabInfo => {
          allNewFormData[tabInfo.sheet_name] = tabInfo.fields.reduce((acc, field) => {
            // acc[field.name] = field.select_value || "";
            acc[field.name] = !isEmptyValue(field.select_value)
              ? field.select_value
              : field.default ?? "";
            return acc;
          }, {});
        });
        setFormData(allNewFormData);

        setChangedFields({});
      }

      // Highlight logic: only after update
      if (justUpdatedRef.current) {
        const showTab = !changeActiveTab && activeTab && tabData[activeTab]
          ? activeTab
          : firstKey;
        const currentFields = (showTab && tabData[showTab]
          ? tabData[showTab].fields
          : []);
        const currentFieldNames = new Set(currentFields.map(f => f.name));
        const prevNames = prevFieldNamesRef.current[activeTabSheet] || new Set();
        const newFields = [];
        currentFieldNames.forEach(name => {
          if (!prevNames.has(name)) newFields.push(name);
        });
        setNewFieldNames(new Set(newFields));
        justUpdatedRef.current = false;

        // Reset checked state for new fields in current tab
        setCheckedNewFields(prev => ({
          ...prev,
          [tabData[activeTab]?.sheet_name || activeTabSheet]: {}
        }));
        // Don't reset seenNewFields! It remembers "ever seen" forever
      }
    } catch (err) {
      console.error("Failed to fetch entity tabs and form data:", err);
    } finally {
      setIsLoadingForm(false);
    }
  };

  useEffect(() => {
    loadTabs();
    // eslint-disable-next-line
  }, [entityId]);

  const captureChangedData = () => {
    return changedFields[activeTabSheet] || {};
  };

  const handleTabClick = (tabName) => {
    if (isLoadingForm || tabName === activeTab) return;

    if (!ignoreNewFieldWarningOnce) {
      // Check for new fields as before
      const currentNewFields = [...newFieldNames].filter(f =>
        formFields.some(ff => ff.name === f)
      );
      const uncheckedNewFields = currentNewFields.filter(
        (f) => !(checkedNewFields[activeTabSheet]?.[f])
      );
      const seenInTab = seenNewFields[activeTabSheet] || {};
      const uncheckedAndNotSeen = uncheckedNewFields.filter(
        (f) => !seenInTab[f]
      );
      const localNewFieldCount = uncheckedAndNotSeen.length;

      if (localNewFieldCount > 0) {
        setShowNewFieldWarning(true);
        setPendingTabChange(tabName); // store tab to switch to
        return;
      }
    }

    setIgnoreNewFieldWarningOnce(false);


    captureChangedData();

    const tabInfo = tabs[tabName];
    setActiveTab(tabName);
    setActiveTabSheet(tabInfo?.sheet_name || "");
    setFormFields(tabInfo?.fields || []);

    setFormData(prev => {
      if (prev[tabInfo.sheet_name]) return prev;
      return {
        ...prev,
        [tabInfo.sheet_name]: tabInfo.fields.reduce((acc, field) => {
          // acc[field.name] = field.select_value || "";
          acc[field.name] = !isEmptyValue(field.select_value)
            ? field.select_value
            : field.default ?? "";
          return acc;
        }, {})
      };
    });
  };

  const scrollTabs = (direction) => {
    const scrollAmount = 150;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleNextTab = () => {
    captureChangedData();

    const tabKeys = Object.keys(tabs);
    const currentIndex = tabKeys.indexOf(activeTab);
    const nextIndex = currentIndex + 1;

    if (nextIndex < tabKeys.length) {
      const nextTabName = tabKeys[nextIndex];
      handleTabClick(nextTabName);
    }
  };

  const handleNewFieldWarningProceed = () => {
    setShowNewFieldWarning(false);
    setIgnoreNewFieldWarningOnce(true);
  };

  const handleUpdate = async (e) => {
    if (e) e.preventDefault();
    const payload = {};

    Object.values(tabs).forEach(tab => {
      const sheetName = tab.sheet_name;
      const fields = tab.fields || [];

      fields.forEach(field => {

        const isHidden = field.show === "2.0";
        if (isHidden) return;

        let sendValue;
        const wasChanged = changedFields[sheetName]?.hasOwnProperty(field.name);
        const currentValue = formData[sheetName]?.[field.name];

        if (wasChanged) {
          // 1. Include changed fields (user input)
          sendValue = changedFields[sheetName][field.name];
        } else if (!wasChanged && isEmptyValue(currentValue) && !isEmptyValue(field.default)) {
          // 2. Field not changed AND current value is empty AND has a default – include default
          sendValue = field.default;
        } else if (!wasChanged && !isEmptyValue(currentValue)) {
          // 3. Field not changed but has a current value – include current value
          sendValue = currentValue;
        }

        // 4. Skip fields that are empty and not changed and have no default
        if (sendValue !== undefined) {
          // Type casting
          let castVal = sendValue;
          if (field.type === "int") {
            castVal = parseInt(sendValue, 10);
          } else if (field.type === "double") {
            castVal = parseFloat(sendValue);
          }

          if (!payload[sheetName]) payload[sheetName] = [];
          payload[sheetName].push({
            field_name: field.name,
            field_value: castVal,
          });
        }
      });
    });
    prevFieldNamesRef.current[activeTabSheet] = new Set(
      (formFields || []).map(f => f.name)
    );
    justUpdatedRef.current = true;

    setIsUpdatingField(true);

    try {
      await updateEntityForm(payload, entityId);
      setChangedFields({});
      await loadTabs(false);
    } catch (error) {
      console.error("Error updating entity form:", error);
    } finally {
      setIsUpdatingField(false);
    }
  };

  const handleTextInputChange = (field, value) => {
    let convertedValue = value;

    // For numeric fields, replace comma with dot for internal storage consistency
    // but keep as string to allow user to type decimal separator on Safari
    if (field.type === "double" && typeof value === "string") {
      convertedValue = value.replace(",", ".");
    }

    setFormData(prev => ({
      ...prev,
      [activeTabSheet]: {
        ...(prev[activeTabSheet] || {}),
        [field.name]: convertedValue,
      },
    }));

    setChangedFields(prev => ({
      ...prev,
      [activeTabSheet]: {
        ...(prev[activeTabSheet] || {}),
        [field.name]: convertedValue,
      },
    }));
  };

  const groupedData = useMemo(() => {
    const result = {};

    formFields.forEach((field, index) => {
      const section = field.section?.trim() || "No Section";

      if (!result[section]) result[section] = [];

      const styleclass = field.styleclass || "NO_STYLE";
      const hasSameline = styleclass.includes("sameline");
      const hasMultiline = styleclass.includes("multiline");
      const isInlineField = hasSameline || hasMultiline;

      // Check if the PREVIOUS field had sameline/multiline (which affects current field)
      const prevField = index > 0 ? formFields[index - 1] : null;
      const prevStyleclass = prevField ? (prevField.styleclass || "NO_STYLE") : null;
      const prevHasSameline = prevStyleclass ? prevStyleclass.includes("sameline") : false;
      const prevHasMultiline = prevStyleclass ? prevStyleclass.includes("multiline") : false;
      const prevIsInlineField = prevHasSameline;

      const lastGroup = result[section][result[section].length - 1];

      // Updated Rules for grouping:
      // 1. If current field is inline (sameline/multiline) AND previous field was also inline, continue group
      // 2. If current field is inline but previous wasn't, start new group
      // 3. If current field is not inline, always start new group
      // 4. If PREVIOUS field had sameline/multiline, current field should join that group (even if current field doesn't have inline styling)

      const shouldContinueGroup = lastGroup && (
        // Original logic: both current and previous are inline
        (isInlineField && prevIsInlineField && lastGroup.isInlineGroup) ||
        // New logic: previous field had inline styling, so current field should join
        (prevIsInlineField && lastGroup.isInlineGroup)
      );

      if (!shouldContinueGroup) {
        // Create a new group
        result[section].push({
          title: field.title?.trim() || "No Title",
          fields: [field],
          styleclass: styleclass,
          range: field.field_range || "NO_RANGE",
          // Group is inline if current field is inline OR if it will accept following fields due to sameline/multiline
          isInlineGroup: isInlineField
        });
      } else {
        // Add to the existing group
        lastGroup.fields.push(field);
        // Update the group's styleclass to include mixed classes if needed
        if (lastGroup.styleclass !== styleclass) {
          lastGroup.styleclass = "MIXED";
        }
      }
    });

    return result;
  }, [formFields]);
  // console.log("Full groupedData structure:", JSON.stringify(groupedData, null, 2));

  // --- New fields logic: count and scroll (visible-aware, but "seen forever")
  const currentNewFields = [...newFieldNames].filter(f =>
    formFields.some(ff => ff.name === f)
  );
  const uncheckedNewFields = currentNewFields.filter(
    (f) => !(checkedNewFields[activeTabSheet]?.[f])
  );
  // Only count those never seen before (in this tab/sheet)
  const seenInTab = seenNewFields[activeTabSheet] || {};
  const uncheckedAndNotSeen = uncheckedNewFields.filter(
    (f) => !seenInTab[f]
  );
  const newFieldCount = uncheckedAndNotSeen.length;

  // --- Intersection Observer for new fields ---
  useEffect(() => {
    if (newFieldNames.size === 0) {
      return;
    }

    const container = formScrollRef.current;
    if (!container) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        let seenThisBatch = {};
        entries.forEach((entry) => {
          const fname = entry.target.getAttribute('data-fieldname');
          if (!fname) return;
          if (entry.isIntersecting) {
            seenThisBatch[fname] = true;
          }
        });

        // New: for any field that became visible, mark it as seen forever
        if (Object.keys(seenThisBatch).length > 0) {
          setSeenNewFields((old) => {
            const tabSeen = { ...(old[activeTabSheet] || {}) };
            Object.keys(seenThisBatch).forEach(f => {
              tabSeen[f] = true;
            });
            return { ...old, [activeTabSheet]: tabSeen };
          });
        }
      },
      {
        root: container,
        threshold: 0.25,
      }
    );

    uncheckedAndNotSeen.forEach((fname) => {
      const el = document.getElementById("field_" + fname);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [newFieldNames, activeTabSheet, formFields, uncheckedAndNotSeen]);

  // Sticky scrollbar effect
  useEffect(() => {
    const updateStickyScrollbars = () => {
      if (!currentVisibleSection) return;

      const container = horizontalScrollRefs.current[currentVisibleSection];
      if (container) {
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;

        if (scrollWidth > clientWidth) {
          const thumbWidth = Math.max((clientWidth / scrollWidth) * 100, 10);
          const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbWidth);

          setStickyScrollbars({
            [currentVisibleSection]: {
              visible: true,
              thumbWidth,
              thumbLeft,
              scrollWidth,
              clientWidth
            }
          });
        } else {
          setStickyScrollbars({ [currentVisibleSection]: { visible: false } });
        }
      }
    };

    const scrollListeners = [];
    const resizeObservers = [];

    // IntersectionObserver to detect the most visible section
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        let maxVisibleSection = null;
        let maxIntersectionRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxIntersectionRatio) {
            maxIntersectionRatio = entry.intersectionRatio;
            maxVisibleSection = entry.target.getAttribute('data-section');
          }
        });

        if (maxVisibleSection && maxVisibleSection !== currentVisibleSection) {
          setCurrentVisibleSection(maxVisibleSection);
        }
      },
      {
        root: formScrollRef.current,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      }
    );

    // Observe all sections
    Object.keys(groupedData).forEach(sectionName => {
      const sectionElement = document.querySelector(`[data-section="${sectionName}"]`);
      if (sectionElement) {
        sectionObserver.observe(sectionElement);
      }

      const container = horizontalScrollRefs.current[sectionName];
      if (container) {
        container.addEventListener('scroll', updateStickyScrollbars);
        scrollListeners.push({ container, handler: updateStickyScrollbars });

        const resizeObserver = new ResizeObserver(updateStickyScrollbars);
        resizeObserver.observe(container);
        resizeObservers.push(resizeObserver);
      }
    });

    // Initial update
    const timer = setTimeout(() => {
      if (Object.keys(groupedData).length > 0) {
        setCurrentVisibleSection(Object.keys(groupedData)[0]);
        updateStickyScrollbars();
      }
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      sectionObserver.disconnect();
      scrollListeners.forEach(({ container, handler }) => {
        container.removeEventListener('scroll', handler);
      });
      resizeObservers.forEach(observer => {
        observer.disconnect();
      });
    };
  }, [activeTab, formFields.length, currentVisibleSection, groupedData]);

  // Update handleStickyScrollbarClick to use currentVisibleSection
  const handleStickyScrollbarClick = (event) => {
    if (isDragging) return;
    if (event.target.classList.contains('sticky-scrollbar-thumb')) return;

    const container = horizontalScrollRefs.current[currentVisibleSection];
    const scrollbarInfo = stickyScrollbars[currentVisibleSection];

    if (!container || !scrollbarInfo) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercent = clickX / rect.width;

    const maxScrollLeft = scrollbarInfo.scrollWidth - scrollbarInfo.clientWidth;
    const newScrollLeft = clickPercent * maxScrollLeft;

    container.scrollLeft = newScrollLeft;
  };

  // Update handleScrollbarThumbMouseDown to use currentVisibleSection
  const handleScrollbarThumbMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const container = horizontalScrollRefs.current[currentVisibleSection];
    if (!container) return;

    setIsDragging(true);
    setDragStartX(event.clientX);
    setDragStartScrollLeft(container.scrollLeft);

    document.body.classList.add('dragging-scrollbar');
  };

  // Update handleMouseMove to use currentVisibleSection
  const handleMouseMove = useCallback((event) => {
    if (!isDragging) return;

    event.preventDefault();

    const container = horizontalScrollRefs.current[currentVisibleSection];
    const scrollbarInfo = stickyScrollbars[currentVisibleSection];

    if (!container || !scrollbarInfo) return;

    const deltaX = event.clientX - dragStartX;
    const scrollbarWidth = container.clientWidth;
    const deltaPercent = deltaX / scrollbarWidth;
    const maxScrollLeft = scrollbarInfo.scrollWidth - scrollbarInfo.clientWidth;
    const deltaScroll = deltaPercent * maxScrollLeft;

    container.scrollLeft = Math.max(0, Math.min(maxScrollLeft, dragStartScrollLeft + deltaScroll));
  }, [isDragging, dragStartX, dragStartScrollLeft, stickyScrollbars, currentVisibleSection]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Remove class from body
      document.body.classList.remove('dragging-scrollbar');

      setIsDragging(null);
      setDragStartX(0);
      setDragStartScrollLeft(0);
    }
  }, [isDragging]);

  // Add and remove mouse event listeners based on dragging state
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const toggleSectionCollapse = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };
  return (
    <>
      <style>
        {`
          .horizontal-scroll-container {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .horizontal-scroll-container::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
          }
          
          .sticky-scrollbar {
            position: fixed;  // Changed from sticky to fixed for viewport positioning
            bottom: 40px;     // Positioned above the footer (footer is at bottom-0 with padding)
            left: 0;
            right: 0;
            height: 12px;
            background: rgba(0, 0, 0, 0.1);
            border-radius: 6px;
            margin: 10px 0;
            z-index: 999;     // Higher than other elements but below footer (1000)
            pointer-events: auto;
            user-select: none;
          }
          
          body.dragging-scrollbar {
            user-select: none;
            cursor: grabbing !important;
          }
          
          body.dragging-scrollbar * {
            cursor: grabbing !important;
          }
          
          .sticky-scrollbar-thumb {
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            border-radius: 6px;
            cursor: grab;
            transition: background 0.2s;
            user-select: none;
          }
          
          .sticky-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.7);
          }
          
          .sticky-scrollbar-thumb:active,
          .sticky-scrollbar-thumb.dragging {
            cursor: grabbing;
            background: rgba(0, 0, 0, 0.8);
          }
          
          .sticky-scrollbar-track {
            width: 100%;
            height: 100%;
            border-radius: 6px;
            cursor: pointer;
          }
          
          /* Ensure Select dropdowns appear above everything */
          .css-26l3qy-menu,
          .css-1n7v3ny-menu {
            z-index: 10000 !important;
          }
          
          .custom-btn:disabled {
            background-color: #1F2448 !important;
            color: white !important;
          }
        `}
      </style>
      <div style={{ color: '#1E244A', fontWeight: 'bold', fontSize: '28px', marginBottom: '10px', textAlign: 'center' }}>
        {entityInfo.address || '-'}
      </div>
      <div className="mt-4 p-2 rounded" style={{ backgroundColor: '#F1EBE8' }}>
        {/* Tab Navigation */}
        <div className="d-flex align-items-center mb-1">
          <button className="btn btn-outline-secondary me-2" onClick={() => scrollTabs("left")}>‹</button>
          <div
            className="flex-grow-1 overflow-auto"
            style={{ whiteSpace: "nowrap", scrollbarWidth: "none" }}
            ref={scrollContainerRef}
          >
            <ul className="nav nav-tabs" style={{ flexWrap: "nowrap" }}>
              {Object.keys(tabs).map((label) => (
                <li className="nav-item" key={label}>
                  <button
                    className={`nav-link ${activeTab === label ? "active" : ""}`}
                    onClick={() => handleTabClick(label)}
                    disabled={isLoadingForm}
                    style={{ whiteSpace: "nowrap", color: activeTab === label ? '#5B68C5' : '#1F2448', backgroundColor: '#F1EBE8', border: 'none', fontWeight: activeTab === label ? 'bold' : 'normal', fontSize: '18px', borderBottom: activeTab === label ? '1px solid #5B68C5' : 'none' }}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button className="btn btn-outline-secondary ms-2" onClick={() => scrollTabs("right")}>›</button>
        </div>

        {/* New fields count icon */}
        {newFieldCount > 0 && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "#fffde7",
              border: "1px solid #ffe082",
              borderRadius: 18,
              padding: "2px 10px",
              marginLeft: 14,
              cursor: "pointer",
              color: "#f9a825",
              fontWeight: 600,
              fontSize: 15
            }}
            title="Click to go to next new field"
            onClick={() => {
              setTimeout(() => {
                if (uncheckedAndNotSeen[0]) {
                  const el = document.getElementById("field_" + uncheckedAndNotSeen[0]);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }, 10);
            }}
          >
            <svg style={{ marginRight: 6 }} width="20" height="20" fill="#fbc02d" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" stroke="#fbc02d" strokeWidth="2" fill="#fffde7" />
              <text x="10" y="14" textAnchor="middle" fontSize="11" fill="#f9a825" fontWeight="bold">{newFieldCount}</text>
            </svg>
            New Field{newFieldCount > 1 ? "s" : ""}
          </span>
        )}

        {/* Form Content */}
        <div className="tab-content mt-3 p-3" style={{ backgroundColor: '#F1EBE8' }}>
          <div className="d-flex justify-content-center position-relative">
            {(isLoadingForm || isUpdatingField) && (
              <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-1 d-flex justify-content-center align-items-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            <div
              className="col-md-12 p-4"
              ref={formScrollRef}
              style={{
                filter: isLoadingForm ? "blur(3px)" : "none",
                pointerEvents: isLoadingForm ? "none" : "auto",
                transition: "filter 0.3s ease",
                backgroundColor: "transparent"
              }}
            >
              <form>
                {Object.entries(groupedData).map(([sectionName, groups]) => {

                  return (
                    <div key={sectionName} data-section={sectionName} className="mb-5" style={{ backgroundColor: "white", padding: "1rem", borderRadius: "8px", overflowX: "auto", position: "relative" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: collapsedSections[sectionName] ? "0" : "1rem" }}>
                        <h4 style={{ fontWeight: "bold", margin: 0 }}>{sectionName}</h4>
                        <button
                          type="button"
                          onClick={() => toggleSectionCollapse(sectionName)}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "20px",
                            cursor: "pointer",
                            color: "#1F2448",
                            padding: "0",
                            lineHeight: 1
                          }}
                          title={collapsedSections[sectionName] ? "Expand section" : "Collapse section"}
                        >
                          {collapsedSections[sectionName] ? "+" : "−"}
                        </button>
                      </div>

                      {!collapsedSections[sectionName] && (
                        <>
                          {/* Main content */}
                          {groups.map(({ title, fields, isInlineGroup }, index) => {
                            // Use isInlineGroup instead of checking individual fields
                            const shouldUseFlexLayout = isInlineGroup;
                            return (
                              <div key={index} className="mb-4">
                                <div
                                  className={shouldUseFlexLayout ? "d-flex align-items-start" : ""}
                                  style={shouldUseFlexLayout ? {
                                    columnGap: "12px",
                                    rowGap: "12px"
                                  } : {}}
                                >
                                  {fields.map((field) => {
                                    return (
                                      <div
                                        key={field.name}
                                        id={"field_" + field.name}
                                        data-fieldname={field.name}
                                        className={shouldUseFlexLayout ? "" : "mb-3"}
                                        style={{
                                          opacity: field.show === "2.0" ? 0 : 1,
                                          width: "220px",
                                          minWidth: "220px",
                                          flexShrink: 0,
                                          marginRight: "0",
                                          marginBottom: shouldUseFlexLayout ? "0.75rem" : "1rem",
                                          backgroundColor: newFieldNames.has(field.name) ? "#fff9c4" : undefined,
                                          transition: "background-color 0.3s",
                                        }}
                                      >
                                        <label className="form-label">
                                          {field.title}
                                          {field.required && <span className="text-danger"> *</span>}
                                          {field.helptext && (
                                            <>
                                              <span
                                                data-tooltip-id={`helptext-${field.name}`}
                                                data-tooltip-content={field.helptext}
                                                tabIndex={0}
                                                style={{
                                                  marginLeft: "4px",
                                                  transform: "translateY(-1px)",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  outline: "none",
                                                }}
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  fill="currentColor"
                                                  viewBox="0 0 24 24"
                                                  style={{ color: "#007bff" }}
                                                >
                                                  <circle cx="12" cy="12" r="10" stroke="#007bff" strokeWidth="2" fill="none" />
                                                  <line x1="12" y1="8" x2="12" y2="12" stroke="#007bff" strokeWidth="2" />
                                                  <circle cx="12" cy="16" r="1" fill="#007bff" />
                                                </svg>
                                              </span>
                                              <Tooltip
                                                id={`helptext-${field.name}`}
                                                place="top"
                                                effect="solid"
                                                style={{
                                                  maxWidth: 300,
                                                  whiteSpace: "pre-line",
                                                  backgroundColor: "#333",
                                                  color: "#fff",
                                                  padding: "8px 12px",
                                                  borderRadius: "4px",
                                                  fontSize: "13px",
                                                  zIndex: 9999,
                                                }}
                                              />
                                            </>
                                          )}
                                        </label>
                                        {Array.isArray(field.value) ? (
                                          <Select
                                            options={field.value.map((opt) => ({ label: opt, value: opt }))}
                                            value={
                                              field.value.find((opt) => opt === formData[activeTabSheet]?.[field.name])
                                                ? { label: formData[activeTabSheet][field.name], value: formData[activeTabSheet][field.name] }
                                                : null
                                            }
                                            isDisabled={field.editable !== "1"}
                                            isClearable={true}
                                            onChange={(selected) => handleTextInputChange(field, selected?.value || "")}
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                              container: (base) => ({
                                                ...base,
                                                width: "220px",
                                              }),
                                              control: (base, state) => ({
                                                ...base,
                                                border: "none",
                                                borderBottom: "1px solid #ccc",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                paddingRight: 0,
                                                minHeight: "32px",
                                                backgroundColor: "transparent",
                                              }),
                                              indicatorsContainer: (base) => ({
                                                ...base,
                                                padding: 0,
                                              }),
                                              dropdownIndicator: (base) => ({
                                                ...base,
                                                padding: "0 4px",
                                              }),
                                              valueContainer: (base) => ({
                                                ...base,
                                                padding: 0,
                                              }),
                                              menu: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                                position: 'absolute',
                                              }),
                                              menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                              }),
                                            }}
                                          />
                                        ) : (
                                          <input
                                            type={field.show === "2.0" ? "hidden" : "text"}
                                            className="form-control border-0 border-bottom"
                                            value={formData[activeTabSheet]?.[field.name] ?? ""}
                                            required={!!field.required}
                                            disabled={field.editable !== "1"}
                                            onChange={(e) => handleTextInputChange(field, e.target.value)}
                                            inputMode={field.type === "double" ? "decimal" : field.type === "int" ? "numeric" : "text"}
                                            onWheel={(e) => e.target.blur()}
                                            onKeyDown={(e) => {
                                              if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                                                e.preventDefault();
                                              }
                                            }}
                                            style={{
                                              width: "220px",
                                              borderRadius: 0,
                                              borderColor: "#ccc",
                                              boxShadow: "none",
                                              backgroundColor: "white !important"
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                })}

              </form>
            </div>
          </div>
        </div>
        {/* Unsaved Changes Modal */}
        {showUnsavedModal && (
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{
              display: "block",
              background: "rgba(0,0,0,0.35)",
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Unsaved Changes</h5>
                </div>
                <div className="modal-body">
                  <p>
                    You have unsaved changes. Would you like to update or discard
                    your changes before leaving?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleModalDiscard}
                  >
                    Discard
                  </button>
                  <button
                    className="btn btn-primary" style={{ backgroundColor: '#5865F2' }}
                    onClick={handleModalUpdate}
                  >
                    Update &amp; Leave
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showNewFieldWarning && (
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{
              display: "block",
              background: "rgba(0,0,0,0.35)",
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Unseen New Fields</h5>
                </div>
                <div className="modal-body">
                  <p>
                    There are new fields you haven't reviewed yet. Are you sure you want to switch tabs without checking them?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowNewFieldWarning(false);
                      pendingTabChangeRef.current = null;
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary" style={{ backgroundColor: '#5865F2' }}
                    onClick={handleNewFieldWarningProceed}
                  >
                    Proceed Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Footer Buttons */}
      <div
        className="position-fixed bottom-0 start-0 end-0 border-top p-3 d-flex justify-content-center"
        style={{ zIndex: 1000, backgroundColor: '#F1EBE8' }}
      >
        <button
          className="btn me-3"
          disabled={isLoadingForm}
          onClick={handleNextTab}
          style={{
            backgroundColor: 'transparent',
            color: '#1F2448',
            border: '1px solid #E4DEDB',
            borderRadius: '50px',
            padding: '0.75rem 1.5rem',
            fontSize: '1.1rem'
          }}
        >
          Volgende
        </button>
        <button
          className="btn custom-btn"
          disabled={isLoadingForm || !hasChanges()}
          onClick={handleUpdate}
          style={{
            backgroundColor: '#1F2448 !important',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            padding: '0.75rem 1.5rem',
            fontSize: '1.1rem',
            cursor: (isLoadingForm || !hasChanges()) ? 'not-allowed' : 'pointer'
          }}
        >
          Opslaan
        </button>
      </div>
    </>
  );
};

export default EntityForm;